import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Description as InvoiceIcon,
  People as ClientIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useCompany } from '../context/CompanyContext';
import {
  GET_DASHBOARD_STATS,
  GET_ME,
  GET_CURRENCY_RATE_HISTORY,
  GET_ENABLED_CURRENCIES,
  GET_CURRENCY_SYSTEM_STATUS,
} from '../graphql/queries';
import { UPDATE_DASHBOARD_CURRENCY_PREFERENCE } from '../graphql/mutations';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Helper component for Summary Cards with Gradient
const SummaryCard = ({ title, value, icon, color1, color2, subtitle }) => (
  <Card
    sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
      },
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            width: 48,
            height: 48,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.8 }}>
        {subtitle}
      </Typography>

      {/* Decorative Circle */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.1)',
        }}
      />
    </CardContent>
  </Card>
);

// Currency Chart Component
const CurrencyChart = ({ selectedCurrency, onCurrencyChange, currencies, rateData, loading }) => {
  // Calculate change percentage
  const changePercent = rateData && rateData.length >= 2
    ? (((rateData[rateData.length - 1].rate - rateData[0].rate) / rateData[0].rate) * 100).toFixed(2)
    : 0;

  const latestRate = rateData && rateData.length > 0
    ? rateData[rateData.length - 1].rate.toFixed(4)
    : '-';

  // Format data for chart (shorter dates)
  const chartData = rateData?.map(point => ({
    ...point,
    shortDate: point.date.substring(5), // MM-DD format
  })) || [];

  return (
    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChartIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Валутен курс EUR/{selectedCurrency}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={selectedCurrency}
            onChange={(e) => onCurrencyChange(e.target.value)}
            sx={{ borderRadius: 2 }}
          >
            {currencies?.map((currency) => (
              <MenuItem key={currency.code} value={currency.code}>
                {currency.code} ({currency.symbol})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ height: 200, mb: 2 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="shortDate"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 11 }}
                  width={60}
                  tickFormatter={(value) => value.toFixed(3)}
                />
                <Tooltip
                  formatter={(value) => [value.toFixed(4), 'Курс']}
                  labelFormatter={(label) => `Дата: ${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="rate"
                  stroke="#1976d2"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Последен курс: <strong>1 EUR = {latestRate} {selectedCurrency}</strong>
            </Typography>
            <Chip
              label={`${changePercent >= 0 ? '+' : ''}${changePercent}%`}
              color={changePercent >= 0 ? 'success' : 'error'}
              size="small"
              variant="outlined"
            />
          </Box>
        </>
      )}
    </Paper>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { activeCompanyId } = useCompany();

  // User data with subscription info
  const { data: meData } = useQuery(GET_ME);
  const user = meData?.me;
  const subscriptionEndDate = user?.company?.subscriptionEndDate;
  const preferredCurrency = user?.preferredDashboardCurrency || 'USD';

  // Selected currency state
  const [selectedCurrency, setSelectedCurrency] = useState(preferredCurrency);

  // Update selected currency when user preference loads
  useEffect(() => {
    if (preferredCurrency) {
      setSelectedCurrency(preferredCurrency);
    }
  }, [preferredCurrency]);

  // Dashboard stats
  const { data, loading } = useQuery(GET_DASHBOARD_STATS, {
    variables: { companyId: activeCompanyId },
    skip: !activeCompanyId,
    pollInterval: 60000,
  });

  // Enabled currencies for dropdown
  const { data: currenciesData } = useQuery(GET_ENABLED_CURRENCIES);
  const currencies = currenciesData?.enabledGlobalCurrencies?.filter(c => c.code !== 'EUR') || [];

  // Currency rate history for chart
  const { data: rateData, loading: rateLoading } = useQuery(GET_CURRENCY_RATE_HISTORY, {
    variables: { currencyCode: selectedCurrency, days: 30 },
    skip: !selectedCurrency,
  });

  // Mutation to update currency preference
  const [updateCurrencyPreference] = useMutation(UPDATE_DASHBOARD_CURRENCY_PREFERENCE, {
    refetchQueries: [{ query: GET_ME }],
  });

  const handleCurrencyChange = async (newCurrency) => {
    setSelectedCurrency(newCurrency);
    try {
      await updateCurrencyPreference({
        variables: { currencyCode: newCurrency },
      });
    } catch (err) {
      console.error('Failed to update currency preference:', err);
    }
  };

  const stats = data?.dashboardStats || {
    totalInvoices: 0,
    totalClients: 0,
    totalRevenue: 0,
    pendingInvoices: 0,
    recentInvoices: [],
  };

  const { data: currencyData } = useQuery(GET_CURRENCY_SYSTEM_STATUS);
  const baseCurrency = currencyData?.currencySystemStatus?.defaultCurrency || 'лв.';

  const getStatusColor = (status) => {
    switch (status) {
      case 'FINAL': return 'success';
      case 'DRAFT': return 'default';
      case 'CANCELLED': return 'error';
      default: return 'primary';
    }
  };

  // Format subscription date
  const formatSubscriptionDate = () => {
    if (!subscriptionEndDate) return null;
    const date = new Date(subscriptionEndDate);
    return date.toLocaleDateString('bg-BG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Check if subscription is expiring soon (within 7 days)
  const isExpiringSoon = () => {
    if (!subscriptionEndDate) return false;
    const endDate = new Date(subscriptionEndDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  };

  if (loading && !data) return <LinearProgress />;

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Табло
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Добре дошли обратно! Ето какво се случва с вашите финанси.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {user?.company && (
              <Chip
                label={`ID: ${user.company.id}`}
                size="small"
                color="primary"
                variant="outlined"
                title="ID на фирмата - използвайте при заявка за поддръжка"
              />
            )}
            {subscriptionEndDate && (
              <Chip
                icon={<CheckCircleIcon />}
                label={`Активен до: ${formatSubscriptionDate()}`}
                size="small"
                color={isExpiringSoon() ? 'warning' : 'success'}
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/documents/create')}
          size="large"
          sx={{ borderRadius: 2 }}
        >
          Нова фактура
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Приходи"
            value={`${stats.totalRevenue.toFixed(2)} ${baseCurrency}`}
            subtitle="Общо приходи за периода"
            icon={<MoneyIcon />}
            color1="#10b981"
            color2="#059669"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Фактури"
            value={stats.totalInvoices}
            subtitle={`${stats.pendingInvoices} чакащи плащане`}
            icon={<InvoiceIcon />}
            color1="#3b82f6"
            color2="#2563eb"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Клиенти"
            value={stats.totalClients}
            subtitle="Активни контрагенти"
            icon={<ClientIcon />}
            color1="#8b5cf6"
            color2="#7c3aed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Закъснели"
            value={stats.overduedInvoices}
            subtitle="Фактури с изтекъл срок"
            icon={<WarningIcon />}
            color1="#f59e0b"
            color2="#d97706"
          />
        </Grid>
      </Grid>

      {/* Recent Activity & Charts Area */}
      <Grid container spacing={3}>
        {/* Recent Invoices Table */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Последни фактури
              </Typography>
              <Button
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/documents')}
                size="small"
              >
                Виж всички
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Номер</TableCell>
                    <TableCell>Клиент</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Сума</TableCell>
                    <TableCell>Статус</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        Няма скорошни фактури
                      </TableCell>
                    </TableRow>
                  ) : (
                    stats.recentInvoices.filter(invoice => invoice != null).map((invoice) => (
                      <TableRow
                        key={invoice.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/documents/${invoice.id}`)}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{invoice.documentNumber}</TableCell>
                        <TableCell>{invoice.clientName}</TableCell>
                        <TableCell>{new Date(invoice.issueDate).toLocaleDateString('bg-BG')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {invoice.totalAmountWithVat?.toFixed(2)} {invoice.currency}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status === 'FINAL' ? 'ИЗДАДЕНА' : invoice.status === 'DRAFT' ? 'ЧЕРНОВА' : 'АНУЛИРАНА'}
                            color={getStatusColor(invoice.status)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Quick Actions / Tips */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'primary.main', color: 'white', mb: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon fontSize="large" />
                <Typography variant="h6" fontWeight="bold">
                  Бърз съвет
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Използвайте новата функция за <strong>UBL Експорт</strong>, за да изпращате електронни фактури директно към счетоводния софтуер на вашите клиенти.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                sx={{ mt: 2, alignSelf: 'flex-start' }}
                onClick={() => navigate('/documents/create')}
              >
                Създай фактура сега
              </Button>
            </Stack>
          </Paper>

          {/* Currency Chart */}
          {currencies.length > 0 && (
            <CurrencyChart
              selectedCurrency={selectedCurrency}
              onCurrencyChange={handleCurrencyChange}
              currencies={currencies}
              rateData={rateData?.currencyRateHistory}
              loading={rateLoading}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
